/**
 * Mangel löschen — nur, was noch nicht beim Kunden ist.
 *
 * Betreiber 06.10.2026: Mitarbeiter löschten versendete offene Mängel
 * bewusst; der Kunde behielt im PDF den Stand „offen“, und im nächsten
 * Bericht fehlte der Mangel kommentarlos (Nachtlauf, Regel
 * `versendeter_mangel_verschwunden`, Nr. 139: 6 Fälle am 06.10.). Ab jetzt:
 *
 *   - steht der Mangel im jüngsten archivierten Bericht, der ihn enthält, als
 *     OFFEN, ist Löschen gesperrt. Ist er heute noch offen, wird statt dessen
 *     „erledigt“ angeboten; hat er schon einen anderen Status, erscheint er
 *     so im nächsten Bericht und kann danach weg.
 *   - steht er in keinem archivierten Bericht (oder im jüngsten mit ihm nicht
 *     offen), darf er verworfen werden.
 *   - lässt sich das Archiv nicht lesen (kein Netz, Bericht ohne lesbare
 *     Einträge), wird das gesagt und „erledigt“ empfohlen; Verwerfen bleibt
 *     möglich — der Nachtlauf meldet einen Irrtum am nächsten Morgen.
 *
 * Quelle ist das Archiv (`report_history.full_report_json -> items`): beim
 * Versand und bei „Nächste Begehung“ wird der Bericht samt Mängeln dort
 * festgehalten. Die Mängel tragen selbst kein „versendet“-Merkmal.
 *
 * Gesucht wird nach der KENNUNG des Mangels über alle Berichte des Projekts
 * (jsonb-Enthaltensein, in der Datenbank) — nicht nach seinem Abschnitt: ein
 * Mangel ohne Abschnitt kann im PDF eines Abschnitts stehen und umgekehrt
 * (Codex-Review 06.10.). Kein Zwischenspeicher: ein gerade versendeter
 * Bericht muss beim nächsten Klick zählen.
 */
import { supabase } from './supabaseClient';
import type { InspectionItem } from '../types';

export interface ArchivTreffer {
    nummer: number;
    datum: string | null;
    /** Status des Mangels in diesem Bericht. */
    status: string;
}

export type LoeschEntscheid =
    /** Offen beim Kunden, heute noch offen → nur „erledigt“. */
    | { art: 'versendet_offen'; nummer: number; datum: string | null }
    /** Offen beim Kunden, heute schon anders → erst nach dem nächsten Bericht. */
    | { art: 'versendet_umgestellt'; nummer: number; datum: string | null }
    /** Noch nicht beim Kunden (oder dort nicht offen) → verwerfen. */
    | { art: 'verwerfen' }
    /** Archiv nicht lesbar → Empfehlung „erledigt“, Verwerfen möglich. */
    | { art: 'unbekannt' };

/** Pure Entscheidung — `treffer`: null = in keinem archivierten Bericht, 'fehler' = nicht feststellbar. */
export const entscheideLoeschen = (
    item: Pick<InspectionItem, 'id' | 'status'>,
    treffer: ArchivTreffer | null | 'fehler',
): LoeschEntscheid => {
    if (treffer === 'fehler') return { art: 'unbekannt' };
    if (!treffer || treffer.status !== 'open') return { art: 'verwerfen' };
    return item.status === 'open'
        ? { art: 'versendet_offen', nummer: treffer.nummer, datum: treffer.datum }
        : { art: 'versendet_umgestellt', nummer: treffer.nummer, datum: treffer.datum };
};

type Zeile = { id?: string; inspection_number: number | null; date: string | null; items: unknown };

/**
 * Der jüngste archivierte Bericht, der den Mangel enthält — `null`, wenn
 * keiner. Wirft, wenn das nicht feststellbar ist (Lesefehler, oder der
 * jüngste Bericht des Projekts trägt keine lesbaren Einträge: dann sagt
 * „nicht gefunden“ nichts).
 */
export const ladeArchivTreffer = async (projectId: string, itemId: string): Promise<ArchivTreffer | null> => {
    const { data, error } = await supabase
        .from('report_history')
        .select('id, inspection_number, date, items:full_report_json->items')
        .eq('project_id', projectId)
        .filter('full_report_json->items', 'cs', JSON.stringify([{ id: itemId }]))
        .order('date', { ascending: false, nullsFirst: false })
        .order('inspection_number', { ascending: false, nullsFirst: false })
        .limit(1);
    if (error) throw error;
    const zeile = ((data || []) as Zeile[])[0];
    if (zeile) {
        const eintrag = (Array.isArray(zeile.items) ? zeile.items : [])
            .find((it) => (it as { id?: unknown })?.id === itemId) as { status?: unknown } | undefined;
        const treffer: ArchivTreffer = {
            nummer: zeile.inspection_number ?? 0,
            datum: zeile.date ?? null,
            status: typeof eintrag?.status === 'string' ? eintrag.status : '',
        };
        // Offen → gesperrt, egal was danach kam. Sonst würde ein NEUERER Bericht
        // ohne lesbare Einträge (der den Mangel offen tragen könnte) übersehen
        // (Codex-Review 06.10., Runde 2): dann ist die Lage unklar.
        if (treffer.status !== 'open') {
            // Zwei Zählungen, weil PostgREST den jsonb-Typ nicht prüfen kann:
            //   (a) `items` fehlt (SQL-NULL);
            //   (b) `items` ist keine Liste (JSON-null, Objekt, Text) — jede
            //       jsonb-Liste enthält die leere Liste, nichts anderes tut das
            //       (`not cs []`). Codex-Review 06.10., Runde 3.
            const neuere = () => {
                let q = supabase
                    .from('report_history')
                    .select('id', { count: 'exact', head: true })
                    .eq('project_id', projectId);
                if (zeile.id) q = q.neq('id', zeile.id);
                if (zeile.date) q = q.gte('date', zeile.date);
                return q;
            };
            const [fehlend, keineListe] = await Promise.all([
                neuere().is('full_report_json->items', null),
                neuere().not('full_report_json->items', 'cs', '[]'),
            ]);
            if (fehlend.error) throw fehlend.error;
            if (keineListe.error) throw keineListe.error;
            if ((fehlend.count ?? 0) + (keineListe.count ?? 0) > 0) {
                throw new Error('Neuerer archivierter Bericht ohne lesbare Einträge.');
            }
        }
        return treffer;
    }

    // Nicht gefunden — das gilt nur, wenn das Archiv auch lesbar ist.
    const { data: juengste, error: fehler2 } = await supabase
        .from('report_history')
        .select('inspection_number, date, items:full_report_json->items')
        .eq('project_id', projectId)
        .order('date', { ascending: false, nullsFirst: false })
        .order('inspection_number', { ascending: false, nullsFirst: false })
        .limit(1);
    if (fehler2) throw fehler2;
    const letzte = ((juengste || []) as Zeile[])[0];
    if (!letzte) return null; // noch kein archivierter Bericht
    if (!Array.isArray(letzte.items)) throw new Error('Jüngster archivierter Bericht ohne lesbare Einträge.');
    return null;
};

/** Prüft einen Eintrag; ohne Projekt (lokal, Vorlage) gibt es kein Archiv. */
export const pruefeLoeschen = async (
    item: Pick<InspectionItem, 'id' | 'status'>,
    projectId?: string | null,
): Promise<LoeschEntscheid> => {
    if (!projectId || projectId === 'temp-id') return { art: 'verwerfen' };
    try {
        return entscheideLoeschen(item, await ladeArchivTreffer(projectId, item.id));
    } catch {
        return entscheideLoeschen(item, 'fehler');
    }
};
